import { Avatar, Button, Dropdown } from "antd";
import { CheckOutlined, DownOutlined } from "@ant-design/icons";
import { useMemo } from "react";
import { isValidEnv, setEnv, setPendingEnv } from "redux/reducers/GlobalUiSlice";

import { Env } from "utils/Types";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { AppURL } from "utils/Actions";
import { useHistory } from "react-router-dom";
import { removeRatedStudent } from "redux/reducers/RatingsSlice";

const EnvLabels: Record<Env, string> = {
  [Env.DOMESTIC]: "Domestic students",
  [Env.INTERNATIONAL]: "International students"
};

const EnvColors: Record<Env, string> = {
  [Env.DOMESTIC]: "#285f15",
  [Env.INTERNATIONAL]: "#A64A95"
};

export type EnvironmentSwitchPropType = {
  collapsed?: boolean;
};

const EnvironmentSwitch = ({ collapsed }: EnvironmentSwitchPropType) => {
  const { env } = useAppSelector(state => state.globalUI);
  const dispatch = useAppDispatch();
  const history = useHistory();

  const items = useMemo(() => {
    const isDomestic = env === Env.DOMESTIC;
    const isInternational = env === Env.INTERNATIONAL;
    return [
      {
        key: "group",
        type: "group",
        label: "Switch workspace",
        children: [
          {
            key: Env.DOMESTIC,
            label: (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontWeight: isDomestic ? 600 : 400
                }}
              >
                {EnvLabels[Env.DOMESTIC]} {isDomestic && <CheckOutlined />}
              </div>
            ),
            style: {
              backgroundColor: isDomestic ? "#E2E3E8" : undefined
            }
          },
          {
            key: Env.INTERNATIONAL,
            label: (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontWeight: isInternational ? 600 : 400
                }}
              >
                {EnvLabels[Env.INTERNATIONAL]} {isInternational && <CheckOutlined />}
              </div>
            ),
            style: {
              backgroundColor: isInternational ? "#E2E3E8" : undefined
            }
          }
        ]
      }
    ];
  }, [env]);

  const avatarSideLength = useMemo(() => {
    if (collapsed) {
      return 28;
    } else {
      return 32;
    }
  }, [collapsed]);

  if (!isValidEnv(env)) {
    return null;
  }
  return (
    <Dropdown
      menu={{
        items,
        onClick: ({ key: newEnv }) => {
          if (newEnv === env) {
            return;
          }
          if (location.pathname.startsWith(AppURL.school.rater.ratings)) {
            // in individual rating form
            dispatch(setPendingEnv(newEnv as Env));
            history.push(AppURL.school.rater.srsApplications);
          } else {
            dispatch(setEnv(newEnv as Env));
            dispatch(removeRatedStudent());
          }
        }
      }}
      placement="bottom"
      overlayStyle={{
        width: 280
      }}
    >
      <Button
        style={{
          width: "100%",
          height: 48,
          padding: 8
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: collapsed ? 2 : 8,
            overflow: "hidden",
            maxWidth: "100%",
            transition: "all 400 ease-in-out"
          }}
        >
          <Avatar
            shape="square"
            style={{
              backgroundColor: EnvColors[env],
              verticalAlign: "middle",
              minWidth: avatarSideLength,
              minHeight: avatarSideLength,
              width: avatarSideLength,
              height: avatarSideLength,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}
          >
            {EnvLabels[env][0]}
          </Avatar>
          {collapsed !== true && (
            <div
              style={{
                textOverflow: "ellipsis",
                fontWeight: "bold",
                maxWidth: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden"
              }}
            >
              {EnvLabels[env]}
            </div>
          )}
          <DownOutlined style={{ flexShrink: 0 }} />
        </div>
      </Button>
    </Dropdown>
  );
};
export default EnvironmentSwitch;
